import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { DetectionEvent, MonitoringStatus } from "@/types";
import type { ClipTrace } from "@/types/clipTrace";
import { IndexedDBStore } from "@/modules/persistence";

// Persist the last monitoring (recording) state so a reload can resume it.
const MONITORING_KEY = "pushka:adminMonitoring";
function saveMonitoringPref(on: boolean): void {
  try {
    localStorage.setItem(MONITORING_KEY, on ? "1" : "0");
  } catch {
    /* ignore (e.g. private-mode quota) */
  }
}
function loadMonitoringPref(): boolean {
  try {
    return localStorage.getItem(MONITORING_KEY) === "1";
  } catch {
    return false;
  }
}

// Stage snapshot for cascade pipeline visualization
export interface StageSnapshot {
  confidence: number;
  detected: boolean | null;
  updatedAt: number;
}

// Cascade metrics for pipeline performance tracking
export interface CascadeMetrics {
  totalFramesProcessed: number;
  avgProcessingTime: number;
  avgStage0Time: number;
  avgStage1Time: number;
  avgStage2Time: number;
  avgStage3Time: number;
  stage0Passed: number;
  stage1Passed: number;
  stage2Passed: number;
  stage3Passed: number;
  stage0Rejected: number;
  stage1Rejected: number;
  stage2Rejected: number;
  stage3Rejected: number;
}

export const useDetectionStore = defineStore("detection", () => {
  const events = ref<DetectionEvent[]>([]);
  const isMonitoring = ref(false);
  const status = ref<MonitoringStatus>("idle");
  const lastConfidence = ref<number | null>(null);
  const detectedCount = ref(0);

  // Cascade pipeline state
  const isFrameProcessing = ref(false);
  const lastCascadeStage = ref<string | null>(null);
  const stageSnapshots = ref<Record<string, StageSnapshot>>({});
  const cascadeMetrics = ref<CascadeMetrics>({
    totalFramesProcessed: 0,
    avgProcessingTime: 0,
    avgStage0Time: 0,
    avgStage1Time: 0,
    avgStage2Time: 0,
    avgStage3Time: 0,
    stage0Passed: 0,
    stage1Passed: 0,
    stage2Passed: 0,
    stage3Passed: 0,
    stage0Rejected: 0,
    stage1Rejected: 0,
    stage2Rejected: 0,
    stage3Rejected: 0,
  });

  const indexedDB = new IndexedDBStore();

  const recentEvents = computed(() => events.value.slice(0, 20));

  const pendingEvents = computed(() => events.value.filter((e) => !e.synced));

  const lastEvent = computed(() =>
    events.value.length > 0 ? events.value[0] : null,
  );

  async function init(): Promise<void> {
    await indexedDB.open();
    events.value = await indexedDB.getAllEvents();
  }

  async function addEvent(event: DetectionEvent): Promise<number> {
    try {
      const id = await indexedDB.saveEvent(event);
      events.value.unshift({
        ...event,
        id,
      });
      detectedCount.value++;
      lastConfidence.value = event.confidence;
      status.value = "detected";
      setTimeout(() => {
        if (status.value === "detected") {
          status.value = isMonitoring.value ? "listening" : "idle";
        }
      }, 2000);
      return id;
    } catch (error) {
      console.error("Failed to add event:", error);
      throw error;
    }
  }

  async function markSynced(ids: number[]): Promise<void> {
    try {
      await indexedDB.markAsSynced(ids);
      ids.forEach((id) => {
        const event = events.value.find((e) => e.id === id);
        if (event) {
          event.synced = true;
        }
      });
    } catch (error) {
      console.error("Failed to mark events as synced:", error);
      throw error;
    }
  }

  /**
   * Записать постоянный статус отправки события (syncStatus/syncError) синхронно в память
   * и в IndexedDB. Используется путём отправки useLiveDetection, чтобы карточка показывала
   * *почему* событие не ушло на сервер, а не только флаг synced.
   */
  async function setSyncStatus(
    id: number,
    status: 'pending' | 'sent' | 'no_location' | 'error',
    error: string | null,
  ): Promise<void> {
    const event = events.value.find((e) => e.id === id);
    if (event) {
      event.syncStatus = status;
      event.syncError = error;
      event.synced = status === 'sent';
    }
    try {
      await indexedDB.updateSyncStatus(id, status, error);
    } catch (e) {
      console.error('Failed to persist sync status:', e);
    }
  }

  async function loadUnsyncedEvents(): Promise<DetectionEvent[]> {
    try {
      return await indexedDB.getUnsyncedEvents();
    } catch (error) {
      console.error("Failed to load unsynced events:", error);
      return [];
    }
  }

  async function clearEvents(): Promise<void> {
    try {
      await indexedDB.clearAll();
      events.value = [];
      detectedCount.value = 0;
      lastConfidence.value = null;
    } catch (error) {
      console.error("Failed to clear events:", error);
      throw error;
    }
  }

  async function deleteEvent(id: number): Promise<void> {
    try {
      await indexedDB.deleteEvent(id);
      events.value = events.value.filter((e) => e.id !== id);
    } catch (error) {
      console.error("Failed to delete event:", error);
      throw error;
    }
  }

  /** Store a local "listen back" audio clip for a detection (local-only, never uploaded). */
  async function attachAudioClip(eventId: number, wav: Blob): Promise<void> {
    try {
      await indexedDB.saveAudioClip(eventId, wav);
    } catch (error) {
      console.error("Failed to attach audio clip:", error);
    }
  }

  /** Read the stored clip for an event, or null if none was captured / it has been pruned. */
  async function getAudioClip(eventId: number): Promise<Blob | null> {
    try {
      return await indexedDB.getAudioClip(eventId);
    } catch (error) {
      console.error("Failed to read audio clip:", error);
      return null;
    }
  }

  async function refreshEvents(): Promise<void> {
    try {
      events.value = await indexedDB.getAllEvents();
    } catch (error) {
      console.error("Failed to refresh events:", error);
      throw error;
    }
  }

  function startMonitoring(): void {
    isMonitoring.value = true;
    status.value = "listening";
    saveMonitoringPref(true);
  }

  function stopMonitoring(): void {
    isMonitoring.value = false;
    status.value = "idle";
    saveMonitoringPref(false);
  }

  /** Whether monitoring was on before the last reload (for auto-resume on startup). */
  function wasMonitoring(): boolean {
    return loadMonitoringPref();
  }

  function updateStatus(newStatus: MonitoringStatus): void {
    status.value = newStatus;
  }

  function updateConfidence(confidence: number): void {
    lastConfidence.value = confidence;
  }

  async function updateEventCluster(
    eventId: number,
    clusterId: string,
  ): Promise<void> {
    try {
      const event = events.value.find((e) => e.id === eventId);
      if (event) {
        event.clusterId = clusterId;
        await indexedDB.updateEventCluster(eventId, clusterId);
      }
    } catch (error) {
      console.error("Failed to update event cluster:", error);
      throw error;
    }
  }

  function getEventsByCluster(clusterId: string): DetectionEvent[] {
    return events.value.filter((e) => e.clusterId === clusterId);
  }

  /**
   * Кэшировать локальный per-window трейс каскада (clipTrace) для события — результат офлайн
   * переанализа 9с клипа фичей «Графики отладки». Записывается в diagnostics.schemaless, чтобы
   * страница графиков работала офлайн и без повторного анализа. Не отправляется автоматически.
   */
  async function setClipTrace(eventId: number, trace: ClipTrace): Promise<void> {
    const event = events.value.find((e) => e.id === eventId);
    if (!event) return;
    if (!event.diagnostics) event.diagnostics = {};
    event.diagnostics.clipTrace = trace;
    try {
      // `event.diagnostics` is a Vue reactive proxy (it lives in `events.value`, a Pinia
      // reactive array). IndexedDB's structured-clone cannot clone proxies — it throws
      // DataCloneError on store.put(). Round-trip through JSON yields a plain object tree
      // with no proxies, which clones cleanly and also drops any non-enumerable cruft.
      const plain = JSON.parse(JSON.stringify(event.diagnostics));
      await indexedDB.updateEventDiagnostics(eventId, plain);
    } catch (e) {
      console.error('Failed to persist clip trace:', e);
    }
  }

  function getClusteredEvents(): DetectionEvent[] {
    return events.value.filter((e) => e.clusterId);
  }

  // Cascade pipeline methods
  function updateStageSnapshot(
    stage: string,
    confidence: number,
    detected: boolean | null,
  ): void {
    stageSnapshots.value[stage] = {
      confidence,
      detected,
      updatedAt: Date.now(),
    };
  }

  function setProcessing(processing: boolean): void {
    isFrameProcessing.value = processing;
  }

  function setLastCascadeStage(stage: string | null): void {
    lastCascadeStage.value = stage;
  }

  function updateCascadeMetrics(
    stage: string,
    passed: boolean,
    processingTime: number,
  ): void {
    const m = cascadeMetrics.value;
    m.totalFramesProcessed++;

    // Update average processing time with exponential moving average
    const alpha = 0.1;
    m.avgProcessingTime = m.avgProcessingTime * (1 - alpha) + processingTime * alpha;

    // Update stage-specific metrics
    const stageKey = stage.replace("_", "") as keyof CascadeMetrics;
    const avgTimeKey = `avg${stage.charAt(0).toUpperCase()}${stage.slice(1).replace("_", "")}Time` as keyof CascadeMetrics;
    const passedKey = `${stageKey}Passed` as keyof CascadeMetrics;
    const rejectedKey = `${stageKey}Rejected` as keyof CascadeMetrics;

    if (avgTimeKey in m && typeof m[avgTimeKey] === "number") {
      (m as Record<string, number>)[avgTimeKey as string] =
        (m as Record<string, number>)[avgTimeKey as string] * (1 - alpha) + processingTime * alpha;
    }

    if (passed) {
      if (passedKey in m && typeof m[passedKey] === "number") {
        (m as Record<string, number>)[passedKey as string]++;
      }
    } else {
      if (rejectedKey in m && typeof m[rejectedKey] === "number") {
        (m as Record<string, number>)[rejectedKey as string]++;
      }
    }
  }

  function getPassRates(): { stage0: number; stage1: number; stage2: number; stage3: number } {
    const m = cascadeMetrics.value;
    const total0 = m.stage0Passed + m.stage0Rejected;
    const total1 = m.stage1Passed + m.stage1Rejected;
    const total2 = m.stage2Passed + m.stage2Rejected;
    const total3 = m.stage3Passed + m.stage3Rejected;

    return {
      stage0: total0 > 0 ? m.stage0Passed / total0 : 0,
      stage1: total1 > 0 ? m.stage1Passed / total1 : 0,
      stage2: total2 > 0 ? m.stage2Passed / total2 : 0,
      stage3: total3 > 0 ? m.stage3Passed / total3 : 0,
    };
  }

  function getPowerSavings(): number {
    const m = cascadeMetrics.value;
    const total = m.totalFramesProcessed;
    if (total === 0) return 0;

    // Calculate power savings based on frames rejected at early stages
    // Stage 0 (energy filter) saves the most power by rejecting silence
    // Stage 1 (anomaly detector) saves moderate power
    const stage0RejectionRate = m.stage0Rejected / total;
    const stage1RejectionRate = m.stage1Rejected / total;

    // Estimate: stage0 saves ~80% of processing power when it rejects
    // stage1 saves ~50% when it rejects
    return (stage0RejectionRate * 80 + stage1RejectionRate * 50) * 100;
  }

  function resetCascadeMetrics(): void {
    cascadeMetrics.value = {
      totalFramesProcessed: 0,
      avgProcessingTime: 0,
      avgStage0Time: 0,
      avgStage1Time: 0,
      avgStage2Time: 0,
      avgStage3Time: 0,
      stage0Passed: 0,
      stage1Passed: 0,
      stage2Passed: 0,
      stage3Passed: 0,
      stage0Rejected: 0,
      stage1Rejected: 0,
      stage2Rejected: 0,
      stage3Rejected: 0,
    };
    stageSnapshots.value = {};
    lastCascadeStage.value = null;
  }

  return {
    events,
    isMonitoring,
    status,
    lastConfidence,
    detectedCount,
    recentEvents,
    pendingEvents,
    lastEvent,
    // Cascade pipeline state
    isFrameProcessing,
    lastCascadeStage,
    stageSnapshots,
    cascadeMetrics,
    init,
    addEvent,
    markSynced,
    setSyncStatus,
    loadUnsyncedEvents,
    clearEvents,
    deleteEvent,
    attachAudioClip,
    getAudioClip,
    refreshEvents,
    startMonitoring,
    stopMonitoring,
    wasMonitoring,
    updateStatus,
    updateConfidence,
    updateEventCluster,
    getEventsByCluster,
    getClusteredEvents,
    setClipTrace,
    // Cascade pipeline methods
    updateStageSnapshot,
    setProcessing,
    setLastCascadeStage,
    updateCascadeMetrics,
    getPassRates,
    getPowerSavings,
    resetCascadeMetrics,
  };
});
